import { Component, Element, h, Host, Prop, Watch, JSX } from '@stencil/core';
import { isMagnoliaEditingMode } from '../../helper';
import { UiCmsFootnoteListBody } from '../ui-cms-footnote-list/ui-cms-footnote-list';
import { Link } from '../ui-cms-link/ui-cms-link';

interface UiCmsTextImageVideoBody {
  // headline properties
  headline?: string;
  headlineLevel?: 2 | 3 | 4;
  icon?: string;
  isHeadlineCentered?: boolean;
  variant?: 'white';

  // image properties
  imageSrc?: string;
  imageAltText?: string;
  imageMouseover?: string;
  imageCopyright?: string;
  imageCaption?: string;
  lightbox?: boolean;

  // video properties
  videoSrc?: string;
  videoCopyright?: string;
  videoLoopSetting?: string;

  // movingImage properties
  movingImageSrc?: string;
  movingImageMasked?: boolean;

  // other properties
  mediaSize?: 'small' | 'medium' | 'large' | 'full';
  text?: string;
  textColumns?: number;
  isTextNotFloating?: boolean;
  leadtext?: string;
  isLeadTextAfterMedia?: boolean;
  linkList?: Link[];
  footnotes?: UiCmsFootnoteListBody;
}

@Component({
  tag: 'ui-cms-text-image-video',
  styleUrl: 'ui-cms-text-image-video.scss',
})
export class UiCmsTextImageVideo {
  @Element() el: HTMLUiCmsTextImageVideoElement;

  @Prop({ mutable: true }) body: UiCmsTextImageVideoBody = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  @Prop({ mutable: true }) i18n = {
    videoEditorInfoMessage: "Im Author-Modus werden Videos in 'Dauerschleife mit Ton' ohne Ton dargestellt.",
  };

  @Prop({ attribute: 'i18n' }) utilityI18n: string;

  @Watch('utilityI18n')
  utilityI18nChangeHandler(newValue: string) {
    if (newValue) this.i18n = { ...this.i18n, ...JSON.parse(newValue) };
  }

  get hasMediaContent(): boolean {
    return !!this.body.imageSrc || !!this.body.videoSrc || !!this.body.movingImageSrc;
  }

  get hasMainContent(): boolean {
    return this.hasMediaContent || (!!this.body.leadtext && this.body.isLeadTextAfterMedia) || !!this.body.text;
  }

  get hasTopContent(): boolean {
    return !!this.body.headline || (!!this.body.leadtext && !this.body.isLeadTextAfterMedia) || !!this.body.icon;
  }

  private refMediaWrapper: HTMLElement;

  private refFigcaption: HTMLElement;

  private resizeObserver: ResizeObserver;

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
    this.utilityI18nChangeHandler(this.utilityI18n);
  }

  componentDidLoad() {
    if (this.body.mediaSize === 'full') {
      this.calculateMarginLeft();

      this.resizeObserver = new ResizeObserver((entries) => {
        entries.forEach(() => {
          this.calculateMarginLeft();
        });
      });
      this.resizeObserver.observe(this.refMediaWrapper);
    }
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  private calculateMarginLeft(): void {
    const marginLeft = this.el.getBoundingClientRect().left;

    this.refMediaWrapper.style.marginLeft = `${-marginLeft}px`;
    if (this.refFigcaption) {
      this.refFigcaption.style.marginLeft = `${marginLeft}px`;
    }
  }

  private renderIcon(): JSX.Element {
    return <ui-icon icon={this.body.icon}></ui-icon>;
  }

  private renderHeadline(): JSX.Element {
    return (
      <ui-headline
        level={this.body.headlineLevel}
        icon={this.body.icon}
        isCentered={this.body.isHeadlineCentered}
        innerHTML={this.body.headline}
      ></ui-headline>
    );
  }

  private renderLeadtext(): JSX.Element {
    return <ui-leadtext innerHTML={this.body.leadtext}></ui-leadtext>;
  }

  private renderImage(): JSX.Element {
    return (
      <ui-image
        class={this.body.mediaSize === 'full' && 'full-width'}
        src={this.body.imageSrc}
        altText={this.body.imageAltText}
        mouseoverText={this.body.imageMouseover}
        copyrightText={this.body.imageCopyright}
        subText={this.body.imageCaption}
        replace="%RENDITION%"
        lightbox={this.body.lightbox}
      >
        {this.body.mediaSize === 'full' && (
          <figcaption
            ref={(el) => {
              this.refFigcaption = el;
            }}
          >
            {this.body.imageCaption && <span innerHTML={this.body.imageCaption}></span>}
            {this.body.imageCopyright && (
              <div class="copy__copyright" innerHTML={`© ${this.body.imageCopyright}`}></div>
            )}
          </figcaption>
        )}
      </ui-image>
    );
  }

  private renderMovingImage(): JSX.Element {
    return (
      <video
        class={{ movingImage: true, 'movingImage--masked': this.body.movingImageMasked }}
        loop
        playsInline
        autoPlay
        muted
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        disablePictureInPicture
      >
        <source src={this.body.movingImageSrc} type="video/mp4" />
        <source src={this.body.movingImageSrc.replace('mp4', 'webm')} type="video/webm" />
      </video>
    );
  }

  private renderVideo(): JSX.Element {
    return (
      <ui-video
        copyright-text={this.body.videoCopyright}
        video-id={this.body.videoSrc}
        looped={this.body.videoLoopSetting !== 'no-loop'}
        muted={isMagnoliaEditingMode() ? true : this.body.videoLoopSetting === 'loop-muted'}
      >
        {isMagnoliaEditingMode() && <ui-alert variant="warning">{this.i18n.videoEditorInfoMessage}</ui-alert>}
      </ui-video>
    );
  }

  render() {
    return (
      <Host>
        <div class={`columns-${this.el.closest('ui-cms-grid-element')?.getAttribute('columns')}`}>
          {this.hasTopContent && (
            <div class="top">
              {this.body.headline && this.renderHeadline()}
              {!this.body.headline && this.body.icon && this.renderIcon()}
              {!this.body.isLeadTextAfterMedia && this.body.leadtext && this.renderLeadtext()}
            </div>
          )}
          {this.hasMainContent && (
            <div class="content">
              <div
                class={{
                  'image-wrapper': this.hasMediaContent,
                  'img-12cols': this.body.mediaSize === 'full',
                  'img-4cols': this.body.mediaSize === 'medium',
                  'img-3cols': this.body.mediaSize === 'small' || !this.body.mediaSize,
                }}
                ref={(el) => {
                  this.refMediaWrapper = el;
                }}
              >
                {this.body.imageSrc && this.renderImage()}
                {this.body.movingImageSrc && this.renderMovingImage()}
                {this.body.videoSrc && this.renderVideo()}
              </div>
              {this.body.isLeadTextAfterMedia && this.body.leadtext && this.renderLeadtext()}
              {this.body.text && (
                <ui-richtext
                  columns={this.body.textColumns}
                  innerHTML={this.body.text}
                  floating={!this.body.isTextNotFloating}
                ></ui-richtext>
              )}
            </div>
          )}
          {this.body.footnotes && <ui-cms-footnote-list body={this.body.footnotes}></ui-cms-footnote-list>}
          {this.body.linkList && <ui-cms-link-list body={{ links: this.body.linkList }}></ui-cms-link-list>}
        </div>
      </Host>
    );
  }
}
